import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
    border,
    colors,
    defaultRadius,
    distances,
    palette,
    placeholderColor,
} from "../../styles/constants";
import { Label, P } from "../Typography";
import { InputValidation } from "./validation";

export type OnChangeFunction = (event: Event) => void;

export interface InputProps {
    name: string;
    label?: any;
    placeholder: string;
    description?: string;
    defaultValue?: any;
    value?: string;
    onChange: (
        e: React.FormEvent<HTMLInputElement> &
            React.ChangeEvent<HTMLInputElement>,
    ) => void;
    onBlur: (
        e: React.FormEvent<HTMLInputElement> &
            React.FocusEvent<HTMLInputElement>,
    ) => void;
    onFocus?: (
        e: React.FormEvent<HTMLInputElement> &
            React.FocusEvent<HTMLInputElement>,
    ) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    type?: string;
    validation?: InputValidation;
    rightAligned?: boolean;
    prefix?: string;
    postfix?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    rightSection?: React.ReactNode;
    autoComplete?: string;
    maxlength?: number;
    className?: string;
    autoFocus?: boolean;
    [key: string]: any;
    readOnly?: boolean;
    required?: boolean;
    mb?: string;
    tooltip?: React.ReactNode;
    style?: React.CSSProperties;
}

const Input = (props: InputProps) => {
    let input: any = null;
    const { t } = useTranslation();

    const whitelistedHtmlProps = [
        "aria-autocomplete",
        "aria-activedescendant",
        "aria-controls",
        "aria-labelledby",
    ];

    interface AnyObject {
        [key: string]: any;
    }

    const otherProps = Object.keys(props)
        .filter((key) => whitelistedHtmlProps.includes(key))
        .reduce((obj: AnyObject, key: string) => {
            obj[key] = props[key];
            return obj;
        }, {});

    const labelExists =
        typeof props.label === "string"
            ? props.label.length > 0
            : !!props.label;

    return (
        <Wrapper className={props.className} style={props.style}>
            {props.label && (
                <Label htmlFor={props.name}>
                    {props.label}
                    {props.required ? "" : t("common.optional")}
                    {props.tooltip}
                </Label>
            )}
            <InputElement
                {...otherProps}
                autoComplete={props.autoComplete}
                autoFocus={props.autoFocus}
                rightAligned={props.rightAligned || false}
                defaultValue={props.defaultValue}
                value={props.value}
                id={props.name}
                type={props.type || "text"}
                name={props.name}
                disabled={props.disabled}
                placeholder={props.placeholder}
                ref={(elem) => {
                    input = elem;
                }}
                onChange={props.onChange}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                onKeyDown={props.onKeyDown}
                className={props.validation ? props.validation.state : ""}
                maxLength={props.maxlength}
                prefix={props.prefix}
                postfix={props.postfix}
                leftIcon={props.leftIcon}
                rightIcon={props.rightIcon}
                $hasRightSection={!!props.rightSection}
                readOnly={props.readOnly}
                mb={props.mb}
            />
            {props.prefix && (
                <Prefix
                    labelExists={labelExists}
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.prefix}
                </Prefix>
            )}
            {props.leftIcon && (
                <LeftIcon
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.leftIcon}
                </LeftIcon>
            )}
            {props.rightSection && (
                <RightSection
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.rightSection}
                </RightSection>
            )}
            {props.rightIcon && (
                <RightIcon
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.rightIcon}
                </RightIcon>
            )}
            {props.postfix && (
                <Postfix
                    labelExists={labelExists}
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.postfix}
                </Postfix>
            )}
            {props.validation && props.validation.message && (
                <Message
                    htmlFor={props.name}
                    className={props.validation.state}
                    labelExists={
                        typeof props.label === "string"
                            ? props.label.length > 0
                            : !!props.label
                    }
                >
                    {props.validation.message}
                </Message>
            )}
            {props.description && (
                <Description>{props.description}</Description>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
    &.stretch {
        width: 100%;
    }
    &.minimal {
        input {
            margin-bottom: 0;
            min-height: 0;
            padding-block: 0;
            border-left: none;
            border-right: none;
            border-top: none;
            border-radius: 0;
            background: transparent;
        }
        label {
            display: none;
        }
        &.as-title {
            input {
                font-size: 16px;
                font-weight: 600;
                padding-inline: 0;
                padding-block: ${distances.tiny};
                border-bottom-color: ${palette.neutral[200]};
                outline: none;
                box-shadow: none;
                &:focus {
                    border-bottom-color: ${palette.neutral[500]};
                }
            }
        }
    }
    &.dynamic-height {
        input {
            margin-bottom: 0;
        }
        label {
            position: unset;
        }
        margin-bottom: ${distances.small};
    }
    &.no-margin {
        input {
            margin-bottom: 0;
        }
        margin-bottom: 0;
    }
    &.dynamic-height-no-margin {
        input {
            margin-bottom: 0;
        }
        label {
            position: unset;
        }
        margin-bottom: 0;
    }
`;

const Description = styled.p`
    color: ${palette.neutral[500]};
    font-size: 14px;
    line-height: 20px;
    margin: 0;
`;

const Prefix = styled.label<{
    labelExists: boolean;
}>`
    position: absolute;
    top: 40px;
    left: ${distances.small};
    color: ${colors.textSecondary};
    cursor: text;

    ${(props) => (!props.labelExists ? "top: 10px;" : "")}
`;
const Postfix = styled.label<{
    labelExists: boolean;
}>`
    position: absolute;
    top: 40px;
    right: ${distances.small};
    color: ${colors.textSecondary};
    cursor: text;

    ${(props) => (!props.labelExists ? "top: 10px;" : "")}
`;
const LeftIcon = styled.label`
    position: absolute;
    top: 30px;
    left: ${distances.nano};
    color: ${palette.neutral[900]};
    fill: ${palette.neutral[900]};
    cursor: text;
`;

const RightIcon = styled.label`
    position: absolute;
    top: 30px;
    right: ${distances.nano};
    color: ${palette.neutral[900]};
    fill: ${palette.neutral[900]};
    cursor: text;
`;

const RightSection = styled.div`
    position: absolute;
    top: 30px;
    right: ${distances.nano};
    color: ${palette.neutral[900]};
    fill: ${palette.neutral[900]};
    cursor: text;
`;

interface InputElementProps {
    rightAligned?: boolean;
    prefix?: string;
    postfix?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    /** Transient: reserves room for the absolutely positioned right section. */
    $hasRightSection?: boolean;
    mb?: string;
}

export const InputElement = styled.input<InputElementProps>`
    border: none;
    outline: none;
    border: ${border.normal} solid ${palette.neutral[400]};
    border-radius: ${defaultRadius};
    background: ${colors.background};
    padding: ${distances.tiny} ${distances.small12};
    text-align: ${(props) => (props.rightAligned ? "right" : "left")};
    width: 100%;
    -moz-appearance: textfield;
    margin-bottom: ${(props) => props.mb || distances.small};
    min-height: 40px;
    transition: all 150ms cubic-bezier(0.2, 0, 0.2, 1);

    /* Safari font size fix */
    font-size: 14px;
    line-height: 20px;

    ${(props) => (props.prefix ? `padding-left: calc(${distances.small} + ${props.prefix.length * 12}px);` : "")}
    ${(props) => (props.postfix ? `padding-right: calc(${distances.small} + ${props.postfix.length * 12}px);` : "")}

    ${(props) => (props.leftIcon ? `padding-left: calc(${distances.small} + 20px);` : "")}
    ${(props) => (props.rightIcon ? `padding-right: calc(${distances.small} + 20px);` : "")}

    /*
     * RightSection is absolutely positioned over the input, so without this the
     * value runs underneath it — a selected store name overlapping its own
     * Clear button. Matches the reservation FormattedInput already makes.
     */
    ${(props) => (props.$hasRightSection ? `padding-right: calc(${distances.small} + 80px);` : "")}

    &::-webkit-inner-spin-button {
        display: none;
        -webkit-appearance: none;
    }

    &::placeholder {
        color: ${placeholderColor};
        opacity: 1;
    }

    &:focus {
        outline: none;
        border: ${border.normal} solid ${palette.primary[300]};
        box-shadow: 0 0 0 3px ${palette.primary[100]};

    }
    &.invalid,
    &:focus .invalid {
        border: ${border.normal} solid ${palette.destructive[300]};
    }

    &.invalid:focus {
        box-shadow: 0 0 0 3px ${palette.destructive[100]};
    }

    &.warning,
    &:focus .warning {
        border: ${border.normal} solid ${palette.warning[300]};
    }

    &.warning:focus {
        box-shadow: 0 0 0 3px ${palette.warning[100]};
    }

    &.valid,
    &:focus .valid {
        border: ${border.normal} solid ${palette.success[300]};
    }

    &.valid:focus {
        box-shadow: 0 0 0 3px ${palette.success[100]};
    }

    &:disabled {
        background: ${palette.neutral[50]};
        border: 1px solid ${palette.neutral[200]};
        color: ${palette.neutral[500]};
    }
`;

const Message = styled.label<{ labelExists: boolean }>`
    position: absolute;
    top: ${(props) => (props.labelExists ? "72px" : "33px")};
    left: 0;
    font-size: 12px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }

    &.warning {
        color: ${colors.warning};
    }

    &.valid {
        color: ${colors.valid};
    }
`;

export default Input;
